import { useCallback, useState } from 'react';
import { captureException } from '@sentry/react';
import useBankLinkStepUpGate from 'app/auth/useBankLinkStepUpGate';
import { registerPasskey, isUserCancellation } from 'app/auth/passkeyClient';
import { deviceLabel } from 'app/auth/deviceLabel';
import { recordPasskeyCreated } from 'app/auth/passkeyPromptChoice';
import { useAppSelector } from 'app/store';
import { selectAuth } from 'app/store/authSlice';

export interface CreatePasskeyOutcome {
  /** A new passkey was created and stored server-side. */
  created: boolean;
  /** The authenticator already holds a passkey for this account
   * (InvalidStateError from excludeCredentials) — nothing new was created,
   * but the user IS enrolled on this device. */
  alreadyRegistered?: boolean;
  /** The user dismissed the native sheet — not an error, show nothing. */
  cancelled?: boolean;
}

export interface UseCreatePasskeyResult {
  /**
   * Run the SEE-213 create-passkey flow: gate on a fresh MFA step-up (reusing
   * the SEE-221 OTP gate — same 15-min window), then invoke the native passkey
   * sheet and store the credential. `onDone(outcome)` fires with what happened
   * (see CreatePasskeyOutcome); on a non-cancel failure `error` is also set so
   * the caller can render it.
   */
  create: (onDone: (outcome: CreatePasskeyOutcome) => void) => void;
  /** Render once — the step-up OTP modal shown when the session isn't MFA-fresh. */
  modal: JSX.Element;
  /** True while the native sheet / verification is in flight. */
  creating: boolean;
  /** Non-cancellation error from the last attempt, if any. */
  error: string | null;
}

export function useCreatePasskey(): UseCreatePasskeyResult {
  // SEE-226: record the prompt marker HERE rather than in each caller, so every
  // creation path keeps it in step — the Security tab's "Add passkey" would
  // otherwise leave it false and the post-login prompt would offer to create a
  // passkey the user already has.
  const { user } = useAppSelector(selectAuth);
  const sub = user?.sub;
  const { guard, modal } = useBankLinkStepUpGate({
    introBody:
      'For security, we would like to verify your account. After verification you can proceed with creating your passkey.',
    noPhoneBody:
      'We don’t have a mobile number on your profile, so we can’t text you a verification code. Add a mobile number in your profile, then come back to create your passkey.',
  });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useCallback(
    (onDone: (outcome: CreatePasskeyOutcome) => void) => {
      setError(null);
      // guard() runs proceed immediately if the session is already MFA-fresh
      // (e.g. a passkey/SSO+OTP session), otherwise opens the SMS OTP step-up
      // and only calls proceed after a verified code.
      guard(async () => {
        setCreating(true);
        try {
          // SEE-226: name the credential after the device so the management
          // list is readable. Without this the backend falls back to the
          // generic "Passkey" for every row.
          const result = await registerPasskey(deviceLabel());
          if (!result.verified) {
            setError('We could not create your passkey. Please try again.');
          } else if (sub) {
            recordPasskeyCreated(sub);
          }
          onDone({ created: result.verified });
        } catch (e) {
          if (e instanceof Error && e.name === 'InvalidStateError') {
            // excludeCredentials matched: this authenticator already has a
            // passkey for the account. Treat as enrolled, not as a failure.
            if (sub) recordPasskeyCreated(sub);
            onDone({ created: false, alreadyRegistered: true });
          } else if (isUserCancellation(e)) {
            onDone({ created: false, cancelled: true });
          } else {
            captureException(e, { tags: { area: 'webauthn', flow: 'register' } });
            setError('We could not create your passkey. Please try again.');
            onDone({ created: false });
          }
        } finally {
          setCreating(false);
        }
      });
    },
    [guard, sub]
  );

  return { create, modal, creating, error };
}

export default useCreatePasskey;
